import { useCurrentFrame, interpolate, Easing } from 'remotion';

// Components
import { Polaroid } from '@/components/common/Polaroid';

// Constants
import { POLAROID_ANIM, CLAMP_RIGHT, CARD_DEALING } from '../constants';

interface PolaroidStackProps {
  avatarUrl: string;
}

const POLAROID_LAYOUT = {
  BACKGROUND: { left: 850, top: 220, width: 478, height: 600, rotation: 5 },
  FOREGROUND: { left: 850, top: 220, width: 478, height: 600, rotation: 8 },
} as const;

/**
 * Stack of polaroid photos with card-dealing animation using 3D transforms
 */
export const PolaroidStack = ({ avatarUrl }: PolaroidStackProps) => {
  const frame = useCurrentFrame();

  const createCardAnimation = (cardDelay: number) => {
    const cardFrame = frame - cardDelay;
    const animEnd = POLAROID_ANIM.DURATION;

    // Scale: card grows as it's being dealt
    const scale = interpolate(
      cardFrame,
      [0, animEnd],
      [POLAROID_ANIM.SCALE_START, 1],
      {
        ...CLAMP_RIGHT,
        easing: Easing.out(Easing.cubic),
      },
    );

    // X translation: card slides from center to final position
    const translateX = interpolate(
      cardFrame,
      [0, animEnd],
      [CARD_DEALING.DEALING_START_X, 0],
      {
        ...CLAMP_RIGHT,
        easing: Easing.out(Easing.cubic),
      },
    );

    // Y translation: card rises from bottom
    const translateY = interpolate(
      cardFrame,
      [0, animEnd],
      [CARD_DEALING.DEALING_START_Y, 0],
      {
        ...CLAMP_RIGHT,
        easing: Easing.out(Easing.cubic),
      },
    );

    // Z translation: creates 3D separation between cards
    const translateZ = interpolate(
      cardFrame,
      [0, animEnd],
      [POLAROID_ANIM.TRANSLATE_Z_START, 0],
      {
        ...CLAMP_RIGHT,
        easing: Easing.out(Easing.cubic),
      },
    );

    // Rotation: card spins during dealing motion
    const rotationZ = interpolate(
      cardFrame,
      [0, animEnd],
      [CARD_DEALING.ROTATION_DURING_DEAL, 0],
      {
        ...CLAMP_RIGHT,
        easing: Easing.out(Easing.cubic),
      },
    );

    // Opacity: fade in as card is dealt
    const opacity = interpolate(
      cardFrame,
      [0, animEnd * CARD_DEALING.OPACITY_DURATION_RATIO],
      [0, 1],
      CLAMP_RIGHT,
    );

    return {
      scale,
      translateX,
      translateY,
      translateZ,
      rotationZ,
      opacity,
    };
  };

  const bgCard = createCardAnimation(POLAROID_ANIM.DELAY);
  const fgCard = createCardAnimation(
    POLAROID_ANIM.DELAY + CARD_DEALING.STAGGER,
  );

  return (
    <div style={{ perspective: `${POLAROID_ANIM.PERSPECTIVE}px` }}>
      {/* Background Polaroid - Card 1 */}
      <div
        style={{
          transform: `translate(${bgCard.translateX}px, ${bgCard.translateY}px) translateZ(${bgCard.translateZ}px) scale(${bgCard.scale}) rotateZ(${bgCard.rotationZ}deg)`,
          transformStyle: 'preserve-3d',
          opacity: bgCard.opacity,
        }}
      >
        <Polaroid {...POLAROID_LAYOUT.BACKGROUND} />
      </div>

      {/* Front Polaroid with Avatar - Card 2 */}
      <div
        style={{
          transform: `translate(${fgCard.translateX}px, ${fgCard.translateY}px) translateZ(${fgCard.translateZ}px) scale(${fgCard.scale}) rotateZ(${fgCard.rotationZ}deg)`,
          transformStyle: 'preserve-3d',
          opacity: fgCard.opacity,
        }}
      >
        <Polaroid {...POLAROID_LAYOUT.FOREGROUND} imgSrc={avatarUrl} />
      </div>
    </div>
  );
};
